import React, { useState, useMemo, useEffect } from 'react';
import { Select, Popover, Empty } from 'antd';
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import without from 'lodash/without';
import PropTypes from 'prop-types';
import { useResize } from 'shared/hooks/use-resize';
import styles from './TreeSelect.less';

export function TreeSelect({
  disabled = false,
  multiple = false,
  treeData,
  fieldNames = { label: 'title' },
  value: propValue,
  placeholder,
  onChange,
  getSelectedLabel,
  selectProps,
}) {
  const {
    aspects: { width: containerWidth = 0 },
    ref: containerRef,
  } = useResize();

  const [value, setValue] = useState(propValue);
  const [popoverVisible, setPopoverVisible] = useState();

  const selectOptions = useMemo(() => {
    const getOptionsFromData = (treeData, path = []) => {
      const options = [];

      treeData?.forEach((treeNode) => {
        const fullPath = [...path, treeNode];

        options.push({
          value: treeNode.value,
          label: getSelectedLabel ? getSelectedLabel(fullPath) : treeNode[fieldNames.label],
        });

        if (!isEmpty(treeNode.children)) {
          options.push(...getOptionsFromData(treeNode.children, fullPath));
        }
      });

      return options;
    };

    return getOptionsFromData(treeData);
  }, [treeData]);

  const setSingleValue = (value) => {
    setValue(value);

    if (onChange) {
      onChange(value);
    }
  };

  const setMultipleValue = (value) => {
    setValue((prev) => {
      const valueMap = keyBy(prev);

      if (valueMap[value]) {
        return without(prev, value);
      }

      const newValue = prev ? [...prev] : [];
      newValue.push(value);
      return newValue;
    });
  };

  const handleSelect = (value, hasChildren) => {
    if (multiple) {
      setMultipleValue(value);
      return;
    }

    setSingleValue(value);

    if (!hasChildren) {
      setPopoverVisible(false);
    }
  };

  useEffect(() => {
    setValue(propValue);
  }, [propValue]);

  const { className: selectClassNameFromProps, ...restSelectProps } = selectProps ?? {};

  return (
    <div ref={containerRef}>
      <Popover
        visible={popoverVisible}
        placement="bottom"
        overlayClassName={styles.popoverOverlay}
        overlayStyle={{ width: containerWidth, minWidth: 200 }}
        trigger={disabled ? [] : 'click'}
        onVisibleChange={() => setPopoverVisible()}
        content={
          <TreeContent
            multiple={multiple}
            treeData={treeData}
            fieldNames={fieldNames}
            value={value}
            onSelect={handleSelect}
          />
        }
      >
        <Select
          {...restSelectProps}
          disabled={disabled}
          mode={multiple ? 'multiple' : undefined}
          open={false}
          options={selectOptions}
          placeholder={placeholder}
          value={isEmpty(treeData) ? undefined : value}
          className={classNames(styles.selectInput, selectClassNameFromProps)}
        />
      </Popover>
    </div>
  );
}

TreeSelect.propTypes = {
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  treeData: PropTypes.object,
  fieldNames: PropTypes.shape({
    label: PropTypes.string,
  }),
  value: PropTypes.oneOf([PropTypes.string, PropTypes.arrayOf(PropTypes.string.isRequired)]),
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  getSelectedLabel: PropTypes.func,
  selectProps: PropTypes.object,
};

function TreeContent({ isRoot = true, multiple, treeData, fieldNames, value, onSelect }) {
  if (isEmpty(treeData)) {
    return <Empty />;
  }

  return (
    <div className={styles.treeContent}>
      {treeData.map((treeNode) => (
        <TreeNode
          key={treeNode.value}
          multiple={multiple}
          isRoot={isRoot}
          treeNode={treeNode}
          fieldNames={fieldNames}
          value={value}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

TreeContent.propTypes = {
  isRoot: PropTypes.bool,
  multiple: PropTypes.bool,
  treeData: PropTypes.object.isRequired,
  fieldNames: PropTypes.shape({
    label: PropTypes.string,
  }),
  value: PropTypes.oneOf([PropTypes.string, PropTypes.arrayOf(PropTypes.string.isRequired)]),
  onSelect: PropTypes.func.isRequired,
};

function TreeNode({ isRoot, multiple, treeNode, fieldNames, value, onSelect }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = !isEmpty(treeNode.children);
  const isSelected = multiple ? (value ?? []).includes(treeNode.value) : value === treeNode.value;

  const handleToggle = (e) => {
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  const handleSelect = (e) => {
    if (treeNode.selectable ?? true) {
      onSelect(treeNode.value, hasChildren);
    }

    if (hasChildren) {
      handleToggle(e);
    }
  };

  return (
    <div
      className={classNames(styles.treeNode, {
        [styles.rootNode]: isRoot,
      })}
    >
      <div className={styles.treeNodeItem} onClick={handleSelect}>
        {hasChildren && (
          <div className={styles.arrowIcon} onClick={handleToggle}>
            {isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
          </div>
        )}
        <div
          className={classNames(styles.itemTitle, {
            [styles.selected]: isSelected,
          })}
        >
          {treeNode[fieldNames.label]}
        </div>
      </div>

      {hasChildren && (
        <div
          className={classNames(styles.treeChildren, {
            [styles.expanded]: isExpanded,
          })}
        >
          <TreeContent
            isRoot={false}
            multiple={multiple}
            treeData={treeNode.children}
            fieldNames={fieldNames}
            value={value}
            onSelect={onSelect}
          />
        </div>
      )}
    </div>
  );
}

TreeNode.propTypes = {
  isRoot: PropTypes.bool,
  multiple: PropTypes.bool,
  treeNode: PropTypes.object.isRequired,
  fieldNames: PropTypes.shape({
    label: PropTypes.string,
  }),
  value: PropTypes.oneOf([PropTypes.string, PropTypes.arrayOf(PropTypes.string.isRequired)]),
  onSelect: PropTypes.func.isRequired,
};
